import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  from,
} from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import { removeTypenameFromVariables } from '@apollo/client/link/remove-typename'

import { useAppStore } from '../store/appStore/appStore'

const removeTypenameFromVariablesLink = removeTypenameFromVariables()

/**
 * Helper to check if an error is an abort error.
 * When fetch is aborted, it throws a DOMException with name 'AbortError',
 * not our custom AbortError class. We need to check for both cases.
 */
const isAbortError = (error: unknown): boolean => {
  if (error instanceof AbortError) return true
  if (error instanceof Error && error.name === 'AbortError') return true
  return false
}

const errorLink = onError(
  ({ forward, graphQLErrors, networkError, operation }) => {
    const queryId = operation.operationName ?? ''

    // Handle aborted requests - these are intentional cancellations, not errors
    if (isAbortError(networkError)) {
      // Don't clean up abort controller for abort errors - it's already been handled
      return
    }

    // Clean up abort controller on error (non-abort errors)
    if (graphQLErrors || networkError) {
      if (abortControllers[queryId]) {
        delete abortControllers[queryId]
      }
    }

    if (graphQLErrors) {
      graphQLErrors.forEach(error => {
        console.error('GraphQL error', error)
        // GraphQL errors are logged but not tracked
      })
    }

    if (networkError) {
      console.error('Network error', networkError)
      // Network errors are logged but not tracked
    }
    return forward(operation)
  },
)

export class AbortError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'AbortError'
  }
}

export const apolloClient = new ApolloClient({
  cache: new InMemoryCache({
    resultCaching: false,
  }),
  defaultOptions: {
    query: {
      fetchPolicy: 'no-cache',
      context: {},
    },
  },
  devtools: {
    enabled: true,
    name: 'frontendApi',
  },
  resolvers: {
    JSON: {
      parseValue: value => value,
    },
    JSONObject: {
      parseValue: value => value,
    },
  },
  link: from([
    removeTypenameFromVariablesLink,
    /**
     * Query cancellation link: Ensures that when a new query with the same
     * operation name is sent while a previous one is still in-flight, the
     * previous request is aborted. This prevents race conditions where
     * stale data from earlier requests could overwrite fresher results.
     */
    new ApolloLink((operation, forward) => {
      const queryId = operation.operationName!

      // Abort any existing in-flight request for this query ID.
      // This is unconditional because checking observable loading state
      // has race conditions - the state may not have updated yet when
      // rapid requests come in back-to-back.
      const existingAbortController = abortControllers[queryId]

      existingAbortController?.abort(new AbortError('updating query'))

      const abortController = new AbortController()
      abortControllers[queryId] = abortController

      operation.setContext({
        fetchOptions: {
          signal: abortController.signal,
        },
      })
      return forward(operation)
    }),
    errorLink,
    new ApolloLink((operation, forward) => {
      const token = useAppStore.getState().auth.token
      const ctx = operation.getContext()

      operation.setContext({
        ...ctx,
        headers: {
          ...ctx.headers,
          Authorization: `Bearer ${token}`,
        },
      })
      return forward(operation)
    }),
    new HttpLink({
      uri: operation => {
        const context = operation.getContext()
        const baseUri = context.uri || useAppStore.getState().config.API_URL

        /**
         * This code is to help with Devtools debuging to identify
         * which network requests are which queries.
         */
        const operationType =
          (operation.query.definitions[0] as any)?.operation ?? 'none'
        const operationName = operation.operationName
        const uri = `${baseUri}/graphql?${operationName}=${operationType}`
        return uri
      },
    }),
  ]),
})

export const abortControllers: Record<string, AbortController> = {}
